/**
 * ProtectedRoute Component
 * Route guard that redirects unauthenticated users to login.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useUser } from '@store/authStore';
import { ROUTES } from '@config/constants';

/**
 * Props for ProtectedRoute component.
 */
interface ProtectedRouteProps {
  /** Child components to render if authenticated */
  children: React.ReactNode;
  /** Optional required roles for access */
  requiredRoles?: string[];
}

/**
 * Protects routes from unauthenticated access.
 * Redirects to login page if user is not authenticated.
 * Preserves the attempted URL for post-login redirect.
 */
export const ProtectedRoute = ({ children, requiredRoles }: ProtectedRouteProps) => {
  const isAuthenticated = useIsAuthenticated();
  const user = useUser();
  const location = useLocation();

  if (!isAuthenticated) {
    // Redirect to login while preserving the attempted URL
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Check role-based access if requiredRoles is specified
  if (requiredRoles && requiredRoles.length > 0) {
    const userRole = user?.role;
    if (!userRole || !requiredRoles.includes(userRole)) {
      const defaultRoute =
        userRole === 'admin'
          ? ROUTES.ADMIN_DASHBOARD
          : userRole === 'executive'
            ? ROUTES.EXECUTIVE_DASHBOARD
            : ROUTES.DASHBOARD;
      return <Navigate to={defaultRoute} replace />;
    }
  }

  return <>{children}</>;
};

